import { useEffect, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'store'
import $app from 'store/app'
import $business from 'store/business'
import $resource from 'store/resource'

import App from 'components/App'
import AppointmentHeader from 'components/Appointment/AppointmentHeader'
import AppointmentFooter from 'components/Appointment/AppointmentFooter'
import AppointmentWhat from 'components/Appointment/AppointmentWhat'
import AppointmentWhen from 'components/Appointment/AppointmentWhen'
import AppointmentOrderSuccess from 'components/Appointment/AppointmentOrderSuccess'
import AppointmentWlistSuccess from 'components/Appointment/AppointmentWlistSuccess'


import styles from './AppointmentDialog.module.scss'

export type Props = {
  open: boolean
  onClose: () => void
}

const AppointmentDialog: React.FC<Props> = ({ open, onClose }) => {
  const dispatch = useAppDispatch()
  const step = useAppSelector(({ $order }) => $order.step)
  const selectedCategoryIds = useAppSelector(({ $category }) => $category.selectedIds)
  const month = useAppSelector(({ $day }) => $day.month)
  const wlistEnabled = useAppSelector(({ $wlist }) => $wlist.enabled)
  const payment = useAppSelector(({ $order }) => $order.payment)
  const settings = useAppSelector($business.get.settings)
  const resources = useAppSelector($resource.get.filtered)

  const [scrollDown, setScrollDown] = useState(false)
  const [resourcesBelowFold, setResourcesBelowFold] = useState(false)

  const showScrollArrow = step === 1
    && settings.allow_multiple_services
    && settings.display_resource
    && resources.length > 0
    && resourcesBelowFold

  useEffect(() => {
    if (!open || step !== 1 || !settings.allow_multiple_services || !settings.display_resource || !resources.length) {
      setResourcesBelowFold(false)
      return
    }

    const scroller = document.getElementById('appointment-dialog')
    if (!scroller) {
      setResourcesBelowFold(false)
      return
    }

    const update = () => {
      const target = document.getElementById('appointment-resources')
      if (!target) {
        setResourcesBelowFold(false)
        return
      }

      const scrollerRect = scroller.getBoundingClientRect()
      const targetRect = target.getBoundingClientRect()
      setResourcesBelowFold(targetRect.top >= scrollerRect.bottom - 24)
    }

    update()
    scroller.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)

    const mutationObserver = new MutationObserver(update)
    mutationObserver.observe(scroller, { childList: true, subtree: true })

    const resizeObserver = new ResizeObserver(update)
    if (scroller.firstElementChild) {
      resizeObserver.observe(scroller.firstElementChild)
    }

    return () => {
      scroller.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      mutationObserver.disconnect()
      resizeObserver.disconnect()
    }
  }, [open, step, settings.allow_multiple_services, settings.display_resource, resources.length, selectedCategoryIds])

  const handleScrollDown = (value: boolean) => {
    setScrollDown(value)
  }

  const handleScrollToResources = () => {
    const target = document.getElementById('appointment-resources')
    const scroller = document.getElementById('appointment-dialog')
    if (!target || !scroller) {
      return
    }

    const top = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop
    scroller.scrollTo({
      top: Math.max(0, top - 16),
      behavior: 'smooth',
    })
  }

  const handleClose = () => {
    if (step === 3) {
      dispatch($app.set.reset(true))
    }

    if (onClose) {
      onClose()
    }
  }

  return (
    <App.Dialog
      open={open}
      scrollDown={scrollDown}
      actions={step === 3 ? null : <AppointmentFooter key={step} onClose={handleClose} />}
      scrollDependensies={step === 1 ? selectedCategoryIds : month}
      whiteBack
      scrollId="appointment-dialog"
      doNotClose={payment.iframe !== ''}
      onScrollEnd={handleScrollDown}
      onClose={handleClose}
      overlay={showScrollArrow ? (
        <button type="button" className={styles.scrollArrow} onClick={handleScrollToResources}>
          <App.Icon icon="chevron-down" white size={20} />
        </button>
      ) : null}
    >
      <App.Flex column className={styles.container}>
        {step !== 3 ? <AppointmentHeader onClose={handleClose} /> : null}

        {step === 1 ? <AppointmentWhat onScrollDown={handleScrollDown} /> : null}
        {step === 2 ? <AppointmentWhen onScrollDown={handleScrollDown} /> : null}
        {step === 3 ? (wlistEnabled ? <AppointmentWlistSuccess onClose={handleClose} /> : <AppointmentOrderSuccess onClose={handleClose} />) : null}
      </App.Flex>
    </App.Dialog>
  )
}

export default AppointmentDialog